import React, { useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import {
  ChevronRight,
  ChevronDown,
  Folder,
  FolderOpen,
  FileText,
  Trash2,
  RotateCcw,
  ArrowRight,
  Check,
  Plus,
  AlertCircle,
  RefreshCw,
  X,
} from 'lucide-react';
import { Button } from '../../../../../components/ui/button';
import { ScrollArea } from '../../../../../components/ui/scroll-area';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../../../components/ui/tooltip';
import { cn } from '../../../../../lib/utils';
import NodeAndDetailStatus from '../../enums/NodeAndDetailStatusEnum';

// Conflict Modal for Tasks
function ConflictTaskModal({ isOpen, onClose, conflictedTasks, onReplace }) {
  if (!isOpen || !conflictedTasks) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50">
      <div
        className="bg-background rounded-lg shadow-xl max-w-2xl w-full mx-4 p-6"
        style={{ border: '1px solid hsl(var(--border))' }}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Replace Task?</h3>
          <button type="button" onClick={onClose} className="p-1 rounded-md hover:bg-muted">
            <X className="h-5 w-5" />
          </button>
        </div>

        <p className="text-muted-foreground mb-4">
          Do you want to completely replace your current task?
        </p>

        <div className="flex items-center gap-4 mb-6">
          {/* Current task */}
          <div className="flex-1 p-3 rounded-lg bg-muted/50">
            <p className="text-xs text-muted-foreground mb-1">Current</p>
            <p className="font-medium text-sm">{conflictedTasks.replaceableTask?.name}</p>
            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
              {conflictedTasks.replaceableTask?.successCriteria}
            </p>
          </div>

          <RefreshCw className="h-6 w-6 text-muted-foreground flex-shrink-0" />

          {/* Incoming task */}
          <div className="flex-1 p-3 rounded-lg bg-blue-500/10">
            <p className="text-xs text-blue-600 mb-1">Incoming</p>
            <p className="font-medium text-sm">{conflictedTasks.replacingTask?.name}</p>
            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
              {conflictedTasks.replacingTask?.successCriteria}
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => onReplace(conflictedTasks.replacingTask)}>Replace</Button>
        </div>
      </div>
    </div>
  );
}

ConflictTaskModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  conflictedTasks: PropTypes.shape({
    replaceableTask: PropTypes.shape({
      name: PropTypes.string,
      successCriteria: PropTypes.string,
    }),
    replacingTask: PropTypes.shape({
      name: PropTypes.string,
      successCriteria: PropTypes.string,
    }),
  }),
  onReplace: PropTypes.func.isRequired,
};

ConflictTaskModal.defaultProps = {
  conflictedTasks: null,
};

// Conflict Modal for Nodes
function ConflictNodeModal({ isOpen, onClose, conflictedNodes, onReplace }) {
  if (!isOpen || !conflictedNodes) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50">
      <div
        className="bg-background rounded-lg shadow-xl max-w-3xl w-full mx-4 p-6"
        style={{ border: '1px solid hsl(var(--border))' }}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Replace Folder?</h3>
          <button type="button" onClick={onClose} className="p-1 rounded-md hover:bg-muted">
            <X className="h-5 w-5" />
          </button>
        </div>

        <p className="text-muted-foreground mb-4">
          Do you want to completely replace your current folder?
        </p>

        <div className="flex items-center gap-4 mb-6">
          {/* Current nodes */}
          <div className="flex-1 p-3 rounded-lg bg-muted/50 max-h-60 overflow-y-auto">
            <p className="text-xs text-muted-foreground mb-2">
              Current ({conflictedNodes.replaceableNodes?.length || 0} items)
            </p>
            {conflictedNodes.replaceableNodes?.map((node) => (
              <div key={node.itemId} className="flex items-center gap-2 py-1">
                <Folder className="h-4 w-4 text-amber-500 flex-shrink-0" />
                <span className="text-sm truncate">{node.name}</span>
              </div>
            ))}
          </div>

          <RefreshCw className="h-6 w-6 text-muted-foreground flex-shrink-0" />

          {/* Incoming nodes */}
          <div className="flex-1 p-3 rounded-lg bg-blue-500/10 max-h-60 overflow-y-auto">
            <p className="text-xs text-blue-600 mb-2">
              Incoming ({conflictedNodes.replacingNodes?.length || 0} items)
            </p>
            {conflictedNodes.replacingNodes?.map((node) => (
              <div key={node.itemId} className="flex items-center gap-2 py-1">
                <Folder className="h-4 w-4 text-amber-500 flex-shrink-0" />
                <span className="text-sm truncate">{node.name}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => onReplace(conflictedNodes.replacingNodes[0])}>Replace</Button>
        </div>
      </div>
    </div>
  );
}

ConflictNodeModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  conflictedNodes: PropTypes.shape({
    replaceableNodes: PropTypes.arrayOf(
      PropTypes.shape({
        itemId: PropTypes.string,
        name: PropTypes.string,
      }),
    ),
    replacingNodes: PropTypes.arrayOf(
      PropTypes.shape({
        itemId: PropTypes.string,
        name: PropTypes.string,
      }),
    ),
  }),
  onReplace: PropTypes.func.isRequired,
};

ConflictNodeModal.defaultProps = {
  conflictedNodes: null,
};

function TreeNode({
  node,
  treeData,
  expanded,
  onToggle,
  onAddTask,
  onAddNode,
  onRemoveNode,
  onRemoveDetail,
  onRevertTask,
  onRevertNode,
  isDefault,
  level = 0,
}) {
  const children = treeData.filter((item) => node.children.includes(item.itemId));
  const hasChildren = children.length > 0;
  const hasDetails = node.details && node.details.length > 0;
  const isExpanded = expanded.includes(node.itemId);

  const getStatusColor = (status) => {
    switch (status) {
      case NodeAndDetailStatus.IS_NEW:
        return 'text-green-600 bg-green-500/10';
      case NodeAndDetailStatus.IS_ADDED:
        return 'text-blue-600 bg-blue-500/10';
      case NodeAndDetailStatus.IS_REPLACED:
      case NodeAndDetailStatus.IS_REPLACED_DETAIL:
      case NodeAndDetailStatus.IS_REPLACED_CHILD:
        return 'text-amber-600 bg-amber-500/10';
      default:
        return '';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case NodeAndDetailStatus.IS_NEW:
        return <Plus className="h-3 w-3" />;
      case NodeAndDetailStatus.IS_ADDED:
        return <Check className="h-3 w-3" />;
      case NodeAndDetailStatus.IS_REPLACED:
      case NodeAndDetailStatus.IS_REPLACED_DETAIL:
        return <RotateCcw className="h-3 w-3" />;
      default:
        return null;
    }
  };

  return (
    <div className="select-none">
      {/* Node Header */}
      <div
        className={cn(
          'flex items-center gap-2 py-1.5 px-2 rounded-md cursor-pointer transition-colors group',
          'hover:bg-muted/50',
          getStatusColor(node.status),
        )}
        style={{ paddingLeft: `${level * 20 + 8}px` }}
      >
        {/* Expand/Collapse */}
        <button
          type="button"
          onClick={() => onToggle(node.itemId)}
          className="p-0.5 hover:bg-muted rounded"
        >
          {!(hasChildren || hasDetails) && <span className="w-4" />}
          {(hasChildren || hasDetails) && isExpanded && (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          )}
          {(hasChildren || hasDetails) && !isExpanded && (
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          )}
        </button>

        {/* Icon */}
        {!hasChildren && <FileText className="h-4 w-4 text-muted-foreground" />}
        {hasChildren && isExpanded && <FolderOpen className="h-4 w-4 text-amber-500" />}
        {hasChildren && !isExpanded && <Folder className="h-4 w-4 text-amber-500" />}

        {/* Name */}
        <span className="flex-1 text-sm font-medium truncate">{node.name}</span>

        {/* Status Badge */}
        {node.status && node.status !== NodeAndDetailStatus.NONE && (
          <span
            className={cn(
              'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs',
              getStatusColor(node.status),
            )}
          >
            {getStatusIcon(node.status)}
          </span>
        )}

        {/* Actions */}
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          {isDefault && node.status !== NodeAndDetailStatus.IS_ADDED && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onAddNode(node);
              }}
              className="p-1 rounded hover:bg-primary/10 text-primary"
              title="Add node"
            >
              <ArrowRight className="h-3.5 w-3.5" />
            </button>
          )}
          {!isDefault && (
            <>
              {(node.status === NodeAndDetailStatus.IS_REPLACED ||
                node.status === NodeAndDetailStatus.IS_REPLACED_CHILD) && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRevertNode(node);
                  }}
                  className="p-1 rounded hover:bg-amber-500/10 text-amber-600"
                  title="Revert"
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                </button>
              )}
              {node.status === NodeAndDetailStatus.IS_NEW && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemoveNode(node.itemId);
                  }}
                  className="p-1 rounded hover:bg-destructive/10 text-destructive"
                  title="Remove"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Details - grouped by name */}
      {isExpanded && hasDetails && (
        <div className="ml-6" style={{ paddingLeft: `${level * 20 + 8}px` }}>
          {(() => {
            // Group details by name
            const groupedDetails = node.details.reduce((acc, detail) => {
              const key = detail.name;
              if (!acc[key]) {
                acc[key] = [];
              }
              acc[key].push(detail);
              return acc;
            }, {});

            return Object.entries(groupedDetails).map(([name, details]) => {
              const firstDetail = details[0];
              const count = details.length;
              const allAdded = details.every((d) => d.status === NodeAndDetailStatus.IS_ADDED);
              const hasNew = details.some((d) => d.status === NodeAndDetailStatus.IS_NEW);
              const hasReplaced = details.some(
                (d) =>
                  d.status === NodeAndDetailStatus.IS_REPLACED ||
                  d.status === NodeAndDetailStatus.IS_REPLACED_DETAIL,
              );

              return (
                <div key={firstDetail.itemId} className="block">
                  <TooltipProvider delayDuration={300}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div
                          className={cn(
                            'flex items-center gap-2 py-1.5 px-2 rounded-md text-sm group cursor-default',
                            'hover:bg-muted/30',
                            getStatusColor(firstDetail.status),
                          )}
                        >
                          <FileText className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                          <span className="flex-1 truncate">{name}</span>

                          {count > 1 && (
                            <span className="px-1.5 py-0.5 rounded-full text-xs bg-muted text-muted-foreground font-medium">
                              {count}
                            </span>
                          )}

                          {firstDetail.status &&
                            firstDetail.status !== NodeAndDetailStatus.NONE && (
                              <span
                                className={cn(
                                  'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs',
                                  getStatusColor(firstDetail.status),
                                )}
                              >
                                {getStatusIcon(firstDetail.status)}
                              </span>
                            )}

                          {isDefault && !allAdded && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                details
                                  .filter((d) => d.status !== NodeAndDetailStatus.IS_ADDED)
                                  .forEach((d) => onAddTask(d));
                              }}
                              className="p-1 rounded hover:bg-primary/10 text-primary opacity-0 group-hover:opacity-100 transition-opacity"
                              title={count > 1 ? `Add all ${count} tasks` : 'Add task'}
                            >
                              <ArrowRight className="h-3.5 w-3.5" />
                            </button>
                          )}
                          {!isDefault && (
                            <>
                              {hasReplaced && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    details
                                      .filter(
                                        (d) =>
                                          d.status === NodeAndDetailStatus.IS_REPLACED ||
                                          d.status === NodeAndDetailStatus.IS_REPLACED_DETAIL,
                                      )
                                      .forEach((d) => onRevertTask(d));
                                  }}
                                  className="p-1 rounded hover:bg-amber-500/10 text-amber-600 opacity-0 group-hover:opacity-100 transition-opacity"
                                  title="Revert"
                                >
                                  <RotateCcw className="h-3.5 w-3.5" />
                                </button>
                              )}
                              {hasNew && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    details
                                      .filter((d) => d.status === NodeAndDetailStatus.IS_NEW)
                                      .forEach((d) => onRemoveDetail(d));
                                  }}
                                  className={cn(
                                    'p-1 rounded hover:bg-destructive/10 text-destructive',
                                    'opacity-0 group-hover:opacity-100 transition-opacity',
                                  )}
                                  title="Remove"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </TooltipTrigger>
                      <TooltipContent
                        side="right"
                        align="start"
                        className="max-w-md p-0 bg-popover border shadow-lg"
                      >
                        <div className="p-3">
                          <p className="font-medium text-sm mb-2">
                            {name}
                            {count > 1 && (
                              <span className="ml-2 text-muted-foreground font-normal">
                                ({count} tasks)
                              </span>
                            )}
                          </p>
                          <div className="space-y-2 max-h-72 overflow-y-auto">
                            {details.map((detail, idx) => (
                              <div
                                key={detail.itemId}
                                className="text-xs p-2 rounded bg-muted/50 border border-border/50"
                              >
                                {count > 1 && (
                                  <div className="flex items-center gap-2 mb-1.5">
                                    <span className="font-medium text-muted-foreground">
                                      #{idx + 1}
                                    </span>
                                    {detail.materialType && (
                                      <span className="px-1.5 py-0.5 rounded bg-primary/10 text-primary">
                                        {detail.materialType}
                                      </span>
                                    )}
                                  </div>
                                )}
                                {count === 1 && detail.materialType && (
                                  <div className="flex items-center gap-2 mb-1.5">
                                    <span className="text-muted-foreground">Type:</span>
                                    <span className="px-1.5 py-0.5 rounded bg-primary/10 text-primary">
                                      {detail.materialType}
                                    </span>
                                  </div>
                                )}
                                {detail.successCriteria && (
                                  <p className="text-muted-foreground mb-1">
                                    {detail.successCriteria}
                                  </p>
                                )}
                                {detail.materialName && (
                                  <p className="text-foreground">
                                    <span className="text-muted-foreground">Material: </span>
                                    {detail.materialName}
                                  </p>
                                )}
                                {detail.link && (
                                  <p className="text-primary truncate mt-1">
                                    <span className="text-muted-foreground">Link: </span>
                                    {detail.link}
                                  </p>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              );
            });
          })()}
        </div>
      )}

      {/* Children */}
      {isExpanded &&
        children.map((child) => (
          <TreeNode
            key={child.itemId}
            node={child}
            treeData={treeData}
            expanded={expanded}
            onToggle={onToggle}
            onAddTask={onAddTask}
            onAddNode={onAddNode}
            onRemoveNode={onRemoveNode}
            onRemoveDetail={onRemoveDetail}
            onRevertTask={onRevertTask}
            onRevertNode={onRevertNode}
            isDefault={isDefault}
            level={level + 1}
          />
        ))}
    </div>
  );
}

TreeNode.propTypes = {
  node: PropTypes.shape({
    itemId: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    children: PropTypes.arrayOf(PropTypes.string).isRequired,
    details: PropTypes.arrayOf(PropTypes.shape({})),
    status: PropTypes.string,
  }).isRequired,
  treeData: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  expanded: PropTypes.arrayOf(PropTypes.string).isRequired,
  onToggle: PropTypes.func.isRequired,
  onAddTask: PropTypes.func.isRequired,
  onAddNode: PropTypes.func.isRequired,
  onRemoveNode: PropTypes.func.isRequired,
  onRemoveDetail: PropTypes.func.isRequired,
  onRevertTask: PropTypes.func.isRequired,
  onRevertNode: PropTypes.func.isRequired,
  isDefault: PropTypes.bool,
  level: PropTypes.number,
};

TreeNode.defaultProps = {
  isDefault: false,
  level: 0,
};

function DialogTreeViewForm({
  treeData,
  setTreeData,
  defaultTreeData,
  setDefaultTreeData,
  replacedTasks,
  setReplacedTasks,
  replacedNodes,
  setReplacedNodes,
}) {
  const [commonExpanded, setCommonExpanded] = useState([]);
  const [defaultExpanded, setDefaultExpanded] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [isTaskModalOpened, setIsTaskModalOpened] = useState(false);
  const [conflictedTasks, setConflictedTasks] = useState({});
  const [conflictedNodes, setConflictedNodes] = useState({});
  const [isNodesModalOpened, setIsNodesModalOpened] = useState(false);

  // Helper functions
  const modifyStatus = (status, node) => ({ ...node, status });

  const modifyNodeAndDetailsStatus = (el, status) => {
    return modifyStatus(status, {
      ...el,
      details: el.details.map((detail) => modifyStatus(status, detail)),
    });
  };

  const modifyDetailsStatus = (node, isNewNode) => {
    return node.details.map((detail) =>
      modifyStatus(
        isNewNode ? NodeAndDetailStatus.IS_NEW : NodeAndDetailStatus.IS_REPLACED_DETAIL,
        detail,
      ),
    );
  };

  const getRootNodes = useCallback((data) => {
    const childIds = new Set(data.flatMap((item) => item.children));
    return data.filter((item) => !childIds.has(item.itemId));
  }, []);

  const findNodesToBeRemoved = useCallback((arr, id) => {
    const selectedItem = arr.find((item) => item.itemId === id);
    if (!selectedItem) return [id];

    let resArr = [id];

    if (selectedItem.children.length) {
      const nodeChildren = arr
        .filter((item) => selectedItem.children.includes(item.itemId))
        .map((item) => findNodesToBeRemoved(arr, item.itemId));
      resArr = [...resArr, ...nodeChildren];
    }

    return resArr.flat(Infinity);
  }, []);

  const getNodeParents = useCallback((firstParent, data) => {
    const parents = [];
    let currentParent = data.find((item) => item.children.includes(firstParent.itemId));

    while (currentParent) {
      parents.unshift(currentParent);
      const parentId = currentParent.itemId;
      currentParent = data.find((item) => item.children.includes(parentId));
    }

    return parents;
  }, []);

  const getNodeChildren = useCallback((node, data) => {
    const children = [];

    const recursion = (parent) => {
      parent.children.forEach((childId) => {
        const childData = data.find((el) => el.itemId === childId);
        if (!childData) return;
        children.push(childData);
        recursion(childData);
      });
    };

    recursion(node);
    return children;
  }, []);

  const filterNodesChildren = useCallback((nodes) => {
    return nodes.map((node) => {
      const children = node.children.filter((child) => nodes.some((el) => el.itemId === child));
      return { ...node, children };
    });
  }, []);

  const getParentNodesToBeRemoved = useCallback(
    (id) => {
      const parentNodesToBeRemoved = [];
      const node = treeData.find((el) => el.itemId === id);
      if (!node) return parentNodesToBeRemoved;

      const parents = getNodeParents(node, treeData).reverse();

      parents.some((el) => {
        if (el.children.length === 1) {
          parentNodesToBeRemoved.unshift(el);
          return false;
        }
        return true;
      });

      return parentNodesToBeRemoved;
    },
    [treeData, getNodeParents],
  );

  // Filter default tree by selected department
  const filteredDepartmentTreeData = useMemo(() => {
    if (!selectedDepartment) return [];
    return defaultTreeData
      .filter((treeItem) => {
        const depPath = treeItem.path?.split('/')[0];
        return depPath?.toLowerCase() === selectedDepartment.toLowerCase();
      })
      .map((treeItem) => ({
        ...treeItem,
        path: treeItem.path?.split('/').splice(2).join('/'),
      }));
  }, [defaultTreeData, selectedDepartment]);

  // Group default tasks by department
  const departmentsWithTasks = useMemo(() => {
    const grouped = defaultTreeData.reduce((acc, item) => {
      const department = item.path?.split('/')[0];
      if (department) {
        if (!acc[department]) {
          acc[department] = [];
        }
        acc[department].push(item);
      }
      return acc;
    }, {});
    return grouped;
  }, [defaultTreeData]);

  const departmentList = Object.keys(departmentsWithTasks);

  const filteredDefaultRootNodes = useMemo(() => {
    return getRootNodes(filteredDepartmentTreeData);
  }, [filteredDepartmentTreeData, getRootNodes]);

  const commonRootNodes = useMemo(() => getRootNodes(treeData), [treeData, getRootNodes]);

  const handleToggleCommon = (id) => {
    setCommonExpanded((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));
  };

  const handleToggleDefault = (id) => {
    setDefaultExpanded((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  };

  // Auto scroll helper
  const autoScroll = (node, elementId) => {
    const scrollDelay = (node?.details?.length || 0) * 85;
    setTimeout(() => {
      const element = document.getElementById(elementId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, scrollDelay);
  };

  const updateDetailsStatus = (detail, task) => {
    if (detail.name === task.name && detail.successCriteria === task.successCriteria) {
      return { ...detail, status: NodeAndDetailStatus.NONE };
    }
    return detail;
  };

  const modifyDefaultTreeData = useCallback(
    (fullyAddedNodes) => {
      return defaultTreeData.map((el) => {
        if (fullyAddedNodes.some((item) => item.itemId === el.itemId)) {
          return modifyNodeAndDetailsStatus(el, NodeAndDetailStatus.IS_ADDED);
        }
        return el;
      });
    },
    [defaultTreeData],
  );

  const getNodeTree = useCallback(
    (firstParent, data) => {
      const nodeTree = [firstParent];

      if (firstParent.children.length) {
        const treeDataParent = data.find((el) =>
          el.path?.toLowerCase().includes(firstParent.path?.toLowerCase()),
        );
        if (treeDataParent) {
          nodeTree.push(...getNodeChildren(treeDataParent, data));
        }
      }

      const parentsParent = data.find((el) => el.children.includes(firstParent.itemId));

      if (parentsParent) {
        nodeTree.unshift(...getNodeParents(parentsParent, data), parentsParent);
      }

      return nodeTree;
    },
    [getNodeChildren, getNodeParents],
  );

  const getOppositeFirstParentIndex = useCallback((node, data) => {
    let firstParentIndex = -1;

    function recursion(path) {
      const parentIndex = data.findIndex((el) => {
        const splitPath = path?.toLowerCase().split('/') || [];
        const splitElPath = el.path?.toLowerCase().split('/') || [];
        return splitPath[splitPath.length - 1] === splitElPath[splitElPath.length - 1];
      });

      if (parentIndex > -1) {
        firstParentIndex = parentIndex;
      } else {
        let splitPath = path?.split('/') || [];
        if (
          splitPath[1] &&
          (splitPath[1].toLowerCase() === 'hard-skills' ||
            splitPath[1].toLowerCase() === 'soft-skills')
        ) {
          splitPath = splitPath.slice(2);
        }
        const parentPath = splitPath.slice(0, -1).join('/');
        if (parentPath) {
          recursion(parentPath);
        }
      }
    }

    recursion(node.path);
    return firstParentIndex;
  }, []);

  const getOppositeFirstTaskParentIndex = useCallback((node, data) => {
    let firstParentIndex = -1;
    function recursion(path) {
      const parentIndex = data.findIndex((el) => {
        const parentIndexPath = el.path?.split('/').splice(2).join('/') || '';
        return (
          path?.toLowerCase() === parentIndexPath.toLowerCase() ||
          path?.toLowerCase() === el.path?.toLowerCase()
        );
      });
      if (parentIndex > -1) {
        firstParentIndex = parentIndex;
      } else {
        const parentPath = path?.split('/').slice(0, -1).join('/');
        if (parentPath) {
          recursion(parentPath);
        }
      }
    }

    recursion(node.path);
    return firstParentIndex;
  }, []);

  const setDetailsStatusNone = (defaultData, defaultParentIndex, task) => {
    return defaultData[defaultParentIndex].details.map((detail) =>
      updateDetailsStatus(detail, task),
    );
  };

  const getModifiedDefaultTreeData = (data, modifiedDefaultNodeChildren) => {
    return data.map((node) => {
      const modifiedNode = modifiedDefaultNodeChildren.find((el) => el.itemId === node.itemId);
      return modifiedNode || node;
    });
  };

  // Change default tree data after adding task
  const changeDefaultTreeData = useCallback(
    (modifiedTreeData, defaultParent, task) => {
      const defaultDetailIndex = defaultParent.details.findIndex((el) => {
        return (
          el.name?.toLowerCase() === task.name?.toLowerCase() &&
          el.successCriteria === task.successCriteria &&
          el.materialName === task.materialName
        );
      });

      if (defaultDetailIndex > -1) {
        defaultParent.details.splice(defaultDetailIndex, 1, {
          ...defaultParent.details[defaultDetailIndex],
          status: NodeAndDetailStatus.IS_ADDED,
        });
      }

      const isNodeFullyAdded = (testedNodeId) => {
        const visited = new Set();
        const stack = [testedNodeId];

        while (stack.length > 0) {
          const nodeId = stack.pop();
          visited.add(nodeId);
          const node = defaultTreeData.find((el) => el.itemId === nodeId);

          if (
            node &&
            node.children.every((item) => modifiedTreeData.find((a) => a.itemId === item)) &&
            node.details.every((detail) => detail.status === NodeAndDetailStatus.IS_ADDED)
          ) {
            node.children.forEach((item) => {
              if (!visited.has(item)) {
                stack.push(item);
              }
            });
          } else {
            return false;
          }
        }

        return true;
      };

      const fullyAddedNodes = getNodeTree(defaultParent, modifiedTreeData).filter((el) =>
        isNodeFullyAdded(el.itemId),
      );

      const modifiedDefaultTreeDataResult = modifyDefaultTreeData(fullyAddedNodes);

      setDefaultTreeData(modifiedDefaultTreeDataResult);
    },
    [defaultTreeData, getNodeTree, modifyDefaultTreeData, setDefaultTreeData],
  );

  // Add sub branch
  const addSubBranch = useCallback(
    (defaultParentIndex, lastCommonParentIndex, task) => {
      const defaultNodeParents = getNodeParents(
        filteredDepartmentTreeData[defaultParentIndex],
        filteredDepartmentTreeData,
      );
      const slicePoint =
        defaultNodeParents.findIndex((el) => {
          const splitPath = treeData[lastCommonParentIndex].path?.toLowerCase().split('/') || [];
          const splitElPath = el.path?.toLowerCase().split('/') || [];
          return splitElPath[splitElPath.length - 1] === splitPath[splitPath.length - 1];
        }) + 1;
      const missingDefaultNodeParents = slicePoint ? defaultNodeParents.slice(slicePoint) : [];

      const lastCommonParent = {
        ...treeData[lastCommonParentIndex],
        children: [
          ...treeData[lastCommonParentIndex].children,
          missingDefaultNodeParents.length
            ? missingDefaultNodeParents[0].itemId
            : filteredDepartmentTreeData[defaultParentIndex].itemId,
        ],
      };

      const nodesChildren = filterNodesChildren([
        ...missingDefaultNodeParents,
        { ...filteredDepartmentTreeData[defaultParentIndex], details: [task] },
        ...getNodeChildren(
          filteredDepartmentTreeData[defaultParentIndex],
          filteredDepartmentTreeData,
        ),
      ]);

      const childrenIndex = nodesChildren.findIndex((el) => el.itemId === lastCommonParent.itemId);

      if (childrenIndex > -1) {
        lastCommonParent.children.push(nodesChildren[childrenIndex + 1].itemId);
      }

      const modifiedTreeData = [
        ...treeData.slice(0, lastCommonParentIndex),
        lastCommonParent,
        ...(childrenIndex === -1
          ? nodesChildren.map((el) => modifyNodeAndDetailsStatus(el, NodeAndDetailStatus.IS_NEW))
          : [
              modifyNodeAndDetailsStatus(
                nodesChildren[childrenIndex + 1],
                NodeAndDetailStatus.IS_NEW,
              ),
            ]),
        ...treeData.slice(lastCommonParentIndex + 1),
      ];

      setTreeData(modifiedTreeData);

      const newExpandedIds = [
        ...commonExpanded,
        ...filterNodesChildren([
          ...missingDefaultNodeParents,
          filteredDepartmentTreeData[defaultParentIndex],
          ...getNodeChildren(
            filteredDepartmentTreeData[defaultParentIndex],
            filteredDepartmentTreeData,
          ),
        ]).map((el) => el.itemId),
        treeData[lastCommonParentIndex].itemId,
      ];
      setCommonExpanded(newExpandedIds);

      changeDefaultTreeData(modifiedTreeData, filteredDepartmentTreeData[defaultParentIndex], task);

      autoScroll(treeData[lastCommonParentIndex], task.itemId);
    },
    [
      treeData,
      filteredDepartmentTreeData,
      commonExpanded,
      getNodeParents,
      filterNodesChildren,
      getNodeChildren,
      setTreeData,
      changeDefaultTreeData,
    ],
  );

  // Add full tree branch
  const addFullTreeBranch = useCallback(
    (defaultParentIndex, task) => {
      const parents = getNodeParents(
        filteredDepartmentTreeData[defaultParentIndex],
        filteredDepartmentTreeData,
      );

      const modifiedTreeData = [
        ...treeData,
        ...filterNodesChildren([
          ...parents,
          {
            ...filteredDepartmentTreeData[defaultParentIndex],
            details: [modifyStatus(NodeAndDetailStatus.IS_NEW, task)],
          },
        ]).map((el) => modifyStatus(NodeAndDetailStatus.IS_NEW, el)),
      ];

      setTreeData(modifiedTreeData);

      setCommonExpanded([
        ...commonExpanded,
        ...filterNodesChildren([...parents, filteredDepartmentTreeData[defaultParentIndex]]).map(
          (el) => el.itemId,
        ),
      ]);

      changeDefaultTreeData(modifiedTreeData, filteredDepartmentTreeData[defaultParentIndex], task);
      autoScroll(parents.at(-1), task.itemId);
    },
    [
      treeData,
      filteredDepartmentTreeData,
      commonExpanded,
      getNodeParents,
      filterNodesChildren,
      setTreeData,
      changeDefaultTreeData,
    ],
  );

  // Perform conflicted merge for tasks
  const performConflictedMerge = useCallback(
    (commonParentIndex, defaultParentIndex, task) => {
      const possibleConflictedTask = treeData[commonParentIndex].details.find(
        (detail) =>
          detail.name?.toLowerCase() === task.name?.toLowerCase() &&
          detail.successCriteria === task.successCriteria,
      );

      if (possibleConflictedTask) {
        setIsTaskModalOpened(true);
        setConflictedTasks({ replaceableTask: possibleConflictedTask, replacingTask: task });
        return;
      }

      const updatedTreeData = [...treeData];
      updatedTreeData[commonParentIndex] = {
        ...updatedTreeData[commonParentIndex],
        details: [
          ...updatedTreeData[commonParentIndex].details,
          modifyStatus(NodeAndDetailStatus.IS_NEW, task),
        ],
      };

      setTreeData(updatedTreeData);

      setCommonExpanded([
        ...commonExpanded,
        ...getNodeParents(updatedTreeData[commonParentIndex], updatedTreeData).map(
          (el) => el.itemId,
        ),
        updatedTreeData[commonParentIndex].itemId,
      ]);

      changeDefaultTreeData(
        [...updatedTreeData],
        filteredDepartmentTreeData[defaultParentIndex],
        task,
      );
    },
    [
      treeData,
      commonExpanded,
      getNodeParents,
      setTreeData,
      changeDefaultTreeData,
      filteredDepartmentTreeData,
    ],
  );

  // Add default task
  const handleAddTask = useCallback(
    (task) => {
      const defaultParentIndex = filteredDepartmentTreeData.findIndex((item) =>
        item.details.includes(task),
      );

      if (defaultParentIndex === -1) return;

      const commonParentIndex = treeData.findIndex((el) => {
        const commonParentPath = el.path?.split('/').splice(2).join('/') || '';
        const defaultParentPath = filteredDepartmentTreeData[defaultParentIndex].path || '';
        return (
          commonParentPath.toLowerCase() === defaultParentPath.toLowerCase() ||
          el.path?.toLowerCase() === defaultParentPath.toLowerCase()
        );
      });

      const lastCommonParentIndex = getOppositeFirstTaskParentIndex(
        filteredDepartmentTreeData[defaultParentIndex],
        treeData,
      );

      if (lastCommonParentIndex > -1 && lastCommonParentIndex !== commonParentIndex) {
        addSubBranch(defaultParentIndex, lastCommonParentIndex, task);
      } else if (commonParentIndex === -1) {
        addFullTreeBranch(defaultParentIndex, task);
      } else {
        performConflictedMerge(commonParentIndex, defaultParentIndex, task);
      }
    },
    [
      filteredDepartmentTreeData,
      treeData,
      getOppositeFirstTaskParentIndex,
      addSubBranch,
      addFullTreeBranch,
      performConflictedMerge,
    ],
  );

  // Replace task
  const replaceTask = useCallback(
    (task) => {
      const defaultParentIndex = filteredDepartmentTreeData.findIndex((item) =>
        item.details.includes(task),
      );

      const commonParentIndex = treeData.findIndex((el) => {
        const commonParentPath = el.path?.split('/').splice(2).join('/') || '';
        const defaultParentPath = filteredDepartmentTreeData[defaultParentIndex]?.path || '';
        return (
          commonParentPath.toLowerCase() === defaultParentPath.toLowerCase() ||
          el.path?.toLowerCase() === defaultParentPath.toLowerCase()
        );
      });

      if (commonParentIndex === -1) return;

      const currentTaskIndex = treeData[commonParentIndex].details.findIndex(
        (detail) =>
          detail.name?.toLowerCase() === task.name?.toLowerCase() &&
          detail.successCriteria === task.successCriteria,
      );

      setReplacedTasks([...replacedTasks, treeData[commonParentIndex].details[currentTaskIndex]]);

      const updatedDetails = [...treeData[commonParentIndex].details];
      updatedDetails.splice(
        currentTaskIndex,
        1,
        modifyStatus(NodeAndDetailStatus.IS_REPLACED, task),
      );

      const updatedTreeData = [...treeData];
      updatedTreeData[commonParentIndex] = {
        ...updatedTreeData[commonParentIndex],
        details: updatedDetails,
      };

      setTreeData(updatedTreeData);
      setIsTaskModalOpened(false);

      const commonExpandedIds = getNodeParents(updatedTreeData[commonParentIndex], updatedTreeData)
        .map((parent) => parent.itemId)
        .concat([updatedTreeData[commonParentIndex].itemId]);
      setCommonExpanded([...commonExpanded, ...commonExpandedIds]);

      changeDefaultTreeData(updatedTreeData, defaultTreeData[defaultParentIndex], task);

      autoScroll(updatedTreeData[commonParentIndex], task.itemId);
    },
    [
      filteredDepartmentTreeData,
      treeData,
      replacedTasks,
      defaultTreeData,
      commonExpanded,
      getNodeParents,
      setTreeData,
      setReplacedTasks,
      changeDefaultTreeData,
    ],
  );

  // Remove detail
  const handleRemoveDetail = useCallback(
    (task) => {
      const commonParent = treeData.find((el) => el.details.includes(task));
      if (!commonParent) return;

      const isLastDetail = commonParent.details.length === 1;

      if (isLastDetail) {
        // Remove last detail - need to remove parent nodes too
        const nodesToBeRemoved = getParentNodesToBeRemoved(commonParent.itemId);
        const defaultNodeIndex = defaultTreeData.findIndex(
          (el) => el.itemId === commonParent.itemId,
        );

        if (defaultNodeIndex > -1) {
          const defaultNodeChildren = getNodeChildren(
            defaultTreeData[defaultNodeIndex],
            defaultTreeData,
          );
          const modifiedDefaultTreeData = defaultTreeData.map((el) => {
            const isAncestorOfDetail = [
              defaultTreeData[defaultNodeIndex].itemId,
              ...defaultNodeChildren.map((child) => child.itemId),
              ...nodesToBeRemoved.map((parent) => parent.itemId),
            ].includes(el.itemId);

            if (isAncestorOfDetail) {
              return modifyNodeAndDetailsStatus(el, NodeAndDetailStatus.NONE);
            }
            return el;
          });

          setDefaultTreeData(modifiedDefaultTreeData);
        }

        const allNodesToRemove = [...nodesToBeRemoved, commonParent];
        setTreeData(
          treeData
            .map((node) => ({
              ...node,
              children: node.children.filter(
                (child) => !allNodesToRemove.map((el) => el.itemId).includes(child),
              ),
            }))
            .filter((item) => !allNodesToRemove.map((el) => el.itemId).includes(item.itemId)),
        );
      } else {
        // Remove not last detail
        const modifiedCommonParent = {
          ...commonParent,
          details: commonParent.details.filter((detail) => detail.itemId !== task.itemId),
        };
        const modifiedTreeData = treeData.map((node) =>
          node.itemId === commonParent.itemId ? modifiedCommonParent : node,
        );
        setTreeData(modifiedTreeData);

        const defaultNodeIndex = defaultTreeData.findIndex((el) => {
          const [department] = el.path?.split('/') || [];
          return (
            el.name?.toLowerCase() === commonParent.name?.toLowerCase() &&
            department?.toLowerCase() === selectedDepartment.toLowerCase()
          );
        });

        if (defaultNodeIndex > -1) {
          const modifiedDetails = defaultTreeData[defaultNodeIndex].details.map((detail) =>
            updateDetailsStatus(detail, task),
          );
          const modifiedDefaultNode = {
            ...defaultTreeData[defaultNodeIndex],
            details: modifiedDetails,
          };

          const modifiedDefaultTreeData = defaultTreeData.map((node) => {
            const [department] = node.path?.split('/') || [];
            return node.path?.toLowerCase().includes(commonParent.path?.toLowerCase()) &&
              department === selectedDepartment
              ? modifiedDefaultNode
              : node;
          });
          setDefaultTreeData(modifiedDefaultTreeData);
        }
      }
    },
    [
      treeData,
      defaultTreeData,
      selectedDepartment,
      getParentNodesToBeRemoved,
      getNodeChildren,
      setTreeData,
      setDefaultTreeData,
    ],
  );

  // Revert replaced task
  const handleRevertTask = useCallback(
    (task) => {
      const parent = treeData.find((item) => item.details.includes(task));
      if (!parent) return;

      const currentTaskIndex = parent.details.findIndex(
        (detail) =>
          detail.name?.toLowerCase() === task.name?.toLowerCase() &&
          detail.successCriteria === task.successCriteria,
      );
      const replacedTaskIndex = replacedTasks.findIndex(
        (item) =>
          item.name?.toLowerCase() === task.name?.toLowerCase() &&
          item.successCriteria === task.successCriteria,
      );

      if (replacedTaskIndex === -1) return;

      const updatedParent = {
        ...parent,
        details: [
          ...parent.details.slice(0, currentTaskIndex),
          replacedTasks[replacedTaskIndex],
          ...parent.details.slice(currentTaskIndex + 1),
        ],
      };
      const updatedTreeData = treeData.map((item) => (item === parent ? updatedParent : item));

      const updatedReplacedTasks = [
        ...replacedTasks.slice(0, replacedTaskIndex),
        ...replacedTasks.slice(replacedTaskIndex + 1),
      ];

      setTreeData(updatedTreeData);
      setReplacedTasks(updatedReplacedTasks);

      const defaultParent = defaultTreeData.find((el) => {
        const defaultParentPath = el.path?.split('/').splice(2).join('/') || '';
        return defaultParentPath.toLowerCase() === parent.path?.toLowerCase();
      });

      if (defaultParent) {
        const updatedDefaultParent = {
          ...defaultParent,
          status: NodeAndDetailStatus.NONE,
          details: setDetailsStatusNone(
            defaultTreeData,
            defaultTreeData.indexOf(defaultParent),
            task,
          ),
        };

        const updatedDefaultTreeData = defaultTreeData.map((el) =>
          el === defaultParent ? updatedDefaultParent : el,
        );

        const defaultNodeParents = getNodeParents(defaultParent, defaultTreeData);
        const updatedDefaultTreeDataWithNodeStatus = updatedDefaultTreeData.map((el) =>
          defaultNodeParents
            .filter((item) => item.children.length === 1)
            .map((a) => a.itemId)
            .includes(el.itemId)
            ? modifyStatus(NodeAndDetailStatus.NONE, el)
            : el,
        );

        setDefaultTreeData(updatedDefaultTreeDataWithNodeStatus);
      }
    },
    [
      treeData,
      replacedTasks,
      defaultTreeData,
      getNodeParents,
      setTreeData,
      setReplacedTasks,
      setDefaultTreeData,
    ],
  );

  // Remove tree node
  const handleRemoveNode = useCallback(
    (id) => {
      const removedNodes = findNodesToBeRemoved(treeData, id);
      const parentNodesToBeRemoved = getParentNodesToBeRemoved(id);

      const filteredCommonExpanded = commonExpanded.filter(
        (item) => !removedNodes.concat(parentNodesToBeRemoved.map((p) => p.itemId)).includes(item),
      );
      setCommonExpanded(filteredCommonExpanded);

      const defaultNodeIndex = defaultTreeData.findIndex((el) => el.itemId === id);

      if (defaultNodeIndex > -1) {
        const defaultNodeChildren = getNodeChildren(
          defaultTreeData[defaultNodeIndex],
          defaultTreeData,
        );

        const modifiedDefaultTreeData = defaultTreeData.map((node) => {
          const isTargetNode = [
            defaultTreeData[defaultNodeIndex].itemId,
            ...defaultNodeChildren.map((child) => child.itemId),
            ...parentNodesToBeRemoved.map((parent) => parent.itemId),
          ].includes(node.itemId);
          return isTargetNode ? modifyNodeAndDetailsStatus(node, NodeAndDetailStatus.NONE) : node;
        });
        setDefaultTreeData(modifiedDefaultTreeData);
      }

      const filteredTreeData = treeData.filter((node) => {
        const allNodesToRemove = removedNodes.concat(
          parentNodesToBeRemoved.map((parent) => parent.itemId),
        );
        return !allNodesToRemove.includes(node.itemId);
      });

      setTreeData(filteredTreeData);
    },
    [
      treeData,
      defaultTreeData,
      commonExpanded,
      findNodesToBeRemoved,
      getParentNodesToBeRemoved,
      getNodeChildren,
      setTreeData,
      setDefaultTreeData,
    ],
  );

  // Add default tree node
  const handleAddNode = useCallback(
    (node) => {
      const conflictedNode = treeData.find(
        (el) => el.path === node.path || el.name?.toLowerCase() === node.name?.toLowerCase(),
      );

      if (conflictedNode) {
        // Handle conflicted node
        const replaceableNodes = [conflictedNode, ...getNodeChildren(conflictedNode, treeData)];
        const replacingNodes = filterNodesChildren([
          node,
          ...getNodeChildren(node, defaultTreeData),
        ]);
        setConflictedNodes({ replaceableNodes, replacingNodes });
        setIsNodesModalOpened(true);
        return;
      }

      const lastCommonParentIndex = getOppositeFirstParentIndex(node, treeData);

      if (lastCommonParentIndex > -1) {
        // Handle last common parent
        const lastCommonParent = treeData[lastCommonParentIndex];
        const defaultNodeParents = getNodeParents(node, defaultTreeData);

        const slicePoint =
          defaultNodeParents.findIndex((el) =>
            el.path?.toLowerCase().includes(lastCommonParent.path?.toLowerCase()),
          ) + 1;

        const missingDefaultNodeParents = defaultNodeParents.slice(slicePoint);

        const modifiedLastCommonParent = {
          ...lastCommonParent,
          children: [
            ...lastCommonParent.children,
            missingDefaultNodeParents.length ? missingDefaultNodeParents[0].itemId : node.itemId,
          ],
        };

        const modifiedTreeData = [
          ...treeData.slice(0, lastCommonParentIndex),
          modifiedLastCommonParent,
          ...filterNodesChildren([
            ...missingDefaultNodeParents,
            node,
            ...getNodeChildren(node, defaultTreeData),
          ]).map((el) => modifyNodeAndDetailsStatus(el, NodeAndDetailStatus.IS_NEW)),
          ...treeData.slice(lastCommonParentIndex + 1),
        ];

        setTreeData(modifiedTreeData);

        const newCommonExpanded = filterNodesChildren([
          ...defaultNodeParents,
          modifiedLastCommonParent,
        ]).map((el) => el.itemId);

        setCommonExpanded((prevCommonExpanded) => [...prevCommonExpanded, ...newCommonExpanded]);

        const defaultNodeChildren = getNodeChildren(node, defaultTreeData);
        const defaultNodeChildrenIds = defaultNodeChildren.map((el) => el.itemId);

        const modifiedDefaultTreeData = defaultTreeData.map((el) => {
          if ([node.itemId, ...defaultNodeChildrenIds].includes(el.itemId)) {
            return modifyNodeAndDetailsStatus(el, NodeAndDetailStatus.IS_ADDED);
          }
          return el;
        });

        setDefaultTreeData(modifiedDefaultTreeData);
        autoScroll(node, node.itemId);
      } else {
        // Handle new node
        const defaultNodeChildren = getNodeChildren(node, defaultTreeData);
        const defaultNodeParents = getNodeParents(node, defaultTreeData);

        const newNodes = filterNodesChildren([
          ...defaultNodeParents,
          node,
          ...defaultNodeChildren,
        ]).map((el) => modifyNodeAndDetailsStatus(el, NodeAndDetailStatus.IS_NEW));
        setTreeData([...treeData, ...newNodes]);

        const newExpandedNodes = filterNodesChildren([...defaultNodeParents, node]).map(
          (el) => el.itemId,
        );
        setCommonExpanded([...commonExpanded, ...newExpandedNodes]);

        const defaultNodeChildrenIds = defaultNodeChildren.map((el) => el.itemId);

        const modifiedDefaultTreeData = defaultTreeData
          .map((el) => {
            if ([node.itemId, ...defaultNodeChildrenIds].includes(el.itemId)) {
              return modifyNodeAndDetailsStatus(el, NodeAndDetailStatus.IS_ADDED);
            }
            return el;
          })
          .map((item, _index, array) => {
            if (
              item.children.length &&
              item.children
                .map((child) => array.find((l) => l.itemId === child))
                .every((e) => e?.status === NodeAndDetailStatus.IS_ADDED)
            ) {
              return modifyStatus(NodeAndDetailStatus.IS_ADDED, item);
            }
            return item;
          });

        setDefaultTreeData(modifiedDefaultTreeData);
        autoScroll(node, node.itemId);
      }
    },
    [
      treeData,
      defaultTreeData,
      commonExpanded,
      getNodeChildren,
      getNodeParents,
      filterNodesChildren,
      getOppositeFirstParentIndex,
      setTreeData,
      setDefaultTreeData,
    ],
  );

  // Replace node
  const replaceNode = useCallback(
    (node) => {
      const replaceableNode = conflictedNodes.replaceableNodes.find(
        (el) => el.path === node.path || el.name?.toLowerCase() === node.name?.toLowerCase(),
      );

      if (!replaceableNode) {
        setIsNodesModalOpened(false);
        return;
      }

      const currentNodeIndex = treeData.findIndex((el) => el.itemId === replaceableNode.itemId);
      const replaceableNodeChildren = getNodeChildren(replaceableNode, treeData);

      setReplacedNodes(
        _.unionBy([...replacedNodes, replaceableNode, ...replaceableNodeChildren], 'path'),
      );

      const nodesToBeRemoved = findNodesToBeRemoved(treeData, replaceableNode.itemId);
      const isNewNode = replaceableNode.status === NodeAndDetailStatus.IS_NEW;

      const filteredTreeData = treeData.filter((el) => !nodesToBeRemoved.includes(el.itemId));

      filteredTreeData.splice(currentNodeIndex, 0, {
        ...replaceableNode,
        ...modifyStatus(isNewNode ? NodeAndDetailStatus.IS_NEW : NodeAndDetailStatus.IS_REPLACED, {
          ...node,
          details: modifyDetailsStatus(node, isNewNode),
        }),
        itemId: replaceableNode.itemId,
      });

      const defaultNodeChildren = getNodeChildren(node, defaultTreeData);

      const modifiedReplaceableNodeChildren = defaultNodeChildren.map((el) =>
        modifyStatus(
          isNewNode ? NodeAndDetailStatus.IS_NEW : NodeAndDetailStatus.IS_REPLACED_CHILD,
          {
            ...el,
            details: modifyDetailsStatus(el, isNewNode),
          },
        ),
      );

      filteredTreeData.push(...modifiedReplaceableNodeChildren);

      setTreeData(filteredTreeData);

      const modifiedDefaultNodeChildren = defaultNodeChildren.map((el) =>
        modifyNodeAndDetailsStatus(el, NodeAndDetailStatus.IS_ADDED),
      );
      const modifiedDefaultTreeData = getModifiedDefaultTreeData(defaultTreeData, [
        modifyNodeAndDetailsStatus(node, NodeAndDetailStatus.IS_ADDED),
        ...modifiedDefaultNodeChildren,
      ]);

      setDefaultTreeData(modifiedDefaultTreeData);

      const modifiedCommonExpanded = [
        ...commonExpanded,
        ...getNodeParents(replaceableNode, filteredTreeData).map((parent) => parent.itemId),
        replaceableNode.itemId,
      ];

      setCommonExpanded(modifiedCommonExpanded);
      setIsNodesModalOpened(false);
      autoScroll(node, replaceableNode.itemId);
    },
    [
      conflictedNodes,
      treeData,
      replacedNodes,
      defaultTreeData,
      commonExpanded,
      findNodesToBeRemoved,
      getNodeChildren,
      getNodeParents,
      setTreeData,
      setReplacedNodes,
      setDefaultTreeData,
    ],
  );

  // Revert replaced node
  const handleRevertNode = useCallback(
    (node) => {
      const replacedNode = replacedNodes.find((el) => el.itemId === node.itemId);
      if (!replacedNode) return;

      const replacedNodeIndex = treeData.findIndex((el) => el.itemId === replacedNode.itemId);
      const replacedNodeChildren = getNodeChildren(replacedNode, replacedNodes);
      const nodesToRemoving = findNodesToBeRemoved(treeData, node.itemId);
      const filteredTreeData = treeData.filter((el) => !nodesToRemoving.includes(el.itemId));

      filteredTreeData.splice(replacedNodeIndex, 0, replacedNode);

      [filteredTreeData[replacedNodeIndex], ...replacedNodeChildren].forEach((el) => {
        const replacedIndex = replacedNodes.findIndex((item) => item.itemId === el.itemId);
        setReplacedNodes([...replacedNodes].splice(replacedIndex, 1));

        const modifiedReplacedTasks = [
          ...replacedTasks.filter(
            (replacedTask) => !el.details.some((detail) => detail.itemId === replacedTask.itemId),
          ),
        ];
        setReplacedTasks(modifiedReplacedTasks);
      });

      filteredTreeData.push(...replacedNodeChildren);

      setTreeData(filteredTreeData);

      setCommonExpanded(commonExpanded.filter((el) => !nodesToRemoving.includes(el)));

      const defaultNodeIndex = defaultTreeData.findIndex((el) => el.path === node.path);

      if (defaultNodeIndex > -1) {
        const defaultNodeChildren = getNodeChildren(
          defaultTreeData[defaultNodeIndex],
          defaultTreeData,
        );

        const updatedDefaultTreeData = [...defaultTreeData];
        updatedDefaultTreeData.splice(
          defaultNodeIndex,
          1,
          modifyNodeAndDetailsStatus(defaultTreeData[defaultNodeIndex], NodeAndDetailStatus.NONE),
        );

        const modifiedDefaultNodeChildren = defaultNodeChildren.map((el) => {
          if (replacedTasks.includes(el.details)) {
            return el;
          }
          return modifyNodeAndDetailsStatus(el, NodeAndDetailStatus.NONE);
        });

        const modifiedDefaultTreeData = getModifiedDefaultTreeData(
          updatedDefaultTreeData,
          modifiedDefaultNodeChildren,
        );

        setDefaultTreeData([...modifiedDefaultTreeData]);
      }
    },
    [
      treeData,
      replacedNodes,
      replacedTasks,
      defaultTreeData,
      commonExpanded,
      findNodesToBeRemoved,
      getNodeChildren,
      setTreeData,
      setReplacedNodes,
      setReplacedTasks,
      setDefaultTreeData,
    ],
  );

  return (
    <>
      <div className="flex h-full">
        {/* Current Tree */}
        <div
          className="flex-1 flex flex-col border-r"
          style={{ borderColor: 'hsl(var(--border))' }}
        >
          <div className="px-4 py-3 border-b" style={{ borderColor: 'hsl(var(--border))' }}>
            <div className="flex items-center gap-2">
              <span className="px-2 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                Current
              </span>
              <span className="text-sm text-muted-foreground">
                Your department&apos;s skill tree
              </span>
            </div>
          </div>
          <ScrollArea className="flex-1 p-4">
            {commonRootNodes.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Folder className="h-12 w-12 text-muted-foreground/30 mb-3" />
                <p className="text-muted-foreground">No skills yet</p>
                <p className="text-xs text-muted-foreground/70">
                  Add skills from the incoming templates
                </p>
              </div>
            ) : (
              commonRootNodes.map((node) => (
                <TreeNode
                  key={node.itemId}
                  node={node}
                  treeData={treeData}
                  expanded={commonExpanded}
                  onToggle={handleToggleCommon}
                  onAddTask={handleAddTask}
                  onAddNode={handleAddNode}
                  onRemoveNode={handleRemoveNode}
                  onRemoveDetail={handleRemoveDetail}
                  onRevertTask={handleRevertTask}
                  onRevertNode={handleRevertNode}
                />
              ))
            )}
          </ScrollArea>
        </div>

        {/* Default/Incoming Tree */}
        <div className="flex-1 flex flex-col">
          <div className="px-4 py-3 border-b" style={{ borderColor: 'hsl(var(--border))' }}>
            <div className="flex items-center gap-2">
              <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-500/10 text-blue-600">
                Incoming
              </span>
              {selectedDepartment ? (
                <button
                  type="button"
                  onClick={() => setSelectedDepartment('')}
                  className="text-sm text-primary hover:underline"
                >
                  ← Back to departments
                </button>
              ) : (
                <span className="text-sm text-muted-foreground">Select a department to import</span>
              )}
            </div>
          </div>
          <ScrollArea className="flex-1 p-4">
            {!selectedDepartment ? (
              // Department list
              <div className="space-y-1">
                {departmentList.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <AlertCircle className="h-12 w-12 text-muted-foreground/30 mb-3" />
                    <p className="text-muted-foreground">No templates available</p>
                  </div>
                ) : (
                  departmentList.map((dept) => (
                    <button
                      key={dept}
                      type="button"
                      onClick={() => setSelectedDepartment(dept)}
                      className={cn(
                        'w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left',
                        'hover:bg-muted transition-colors',
                      )}
                    >
                      <Folder className="h-5 w-5 text-amber-500" />
                      <span className="font-medium">{dept}</span>
                      <span className="ml-auto text-xs text-muted-foreground">
                        {departmentsWithTasks[dept]?.length || 0} items
                      </span>
                    </button>
                  ))
                )}
              </div>
            ) : (
              // Filtered tree
              filteredDefaultRootNodes.map((node) => (
                <TreeNode
                  key={node.itemId}
                  node={node}
                  treeData={filteredDepartmentTreeData}
                  expanded={defaultExpanded}
                  onToggle={handleToggleDefault}
                  onAddTask={handleAddTask}
                  onAddNode={handleAddNode}
                  onRemoveNode={handleRemoveNode}
                  onRemoveDetail={handleRemoveDetail}
                  onRevertTask={handleRevertTask}
                  onRevertNode={handleRevertNode}
                  isDefault
                />
              ))
            )}
          </ScrollArea>
        </div>
      </div>

      {/* Conflict Modals */}
      <ConflictTaskModal
        isOpen={isTaskModalOpened}
        onClose={() => setIsTaskModalOpened(false)}
        conflictedTasks={conflictedTasks}
        onReplace={replaceTask}
      />
      <ConflictNodeModal
        isOpen={isNodesModalOpened}
        onClose={() => setIsNodesModalOpened(false)}
        conflictedNodes={conflictedNodes}
        onReplace={replaceNode}
      />
    </>
  );
}

const skillTree = {
  itemId: PropTypes.string,
  name: PropTypes.string,
  details: PropTypes.arrayOf(
    PropTypes.shape({
      itemId: PropTypes.string,
      successCriteria: PropTypes.string,
      materialName: PropTypes.string,
      materialType: PropTypes.string,
      link: PropTypes.string,
      level: PropTypes.string,
    }),
  ),
  children: PropTypes.arrayOf(PropTypes.string).isRequired,
};

DialogTreeViewForm.propTypes = {
  treeData: PropTypes.arrayOf(PropTypes.shape(skillTree)).isRequired,
  setTreeData: PropTypes.func.isRequired,
  defaultTreeData: PropTypes.arrayOf(PropTypes.shape(skillTree)).isRequired,
  setDefaultTreeData: PropTypes.func.isRequired,
  replacedTasks: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  setReplacedTasks: PropTypes.func.isRequired,
  replacedNodes: PropTypes.arrayOf(PropTypes.shape(skillTree)).isRequired,
  setReplacedNodes: PropTypes.func.isRequired,
};

export default DialogTreeViewForm;
